import { GameConfig } from '../../config/gameConfig';
import { MinionInstance } from './Minion';
import { MinionDefinition } from './MinionDefinition';
import { UpgradeBranch } from './UpgradePath';
import { GamePhase } from './GamePhase';
import {
  FORTRESS_ABILITY_ID,
  FORTRESS_HP_BONUS,
  GRANDMASTER_ABILITY_ID,
  TEACHER_ABILITY_ID,
  SPELLMAKER_ABILITY_ID,
  SPELLMAKER_SPELL_POWER,
  SPELLMASTER_ABILITY_ID,
  SPELLMASTER_SPELL_POWER,
} from './AbilityConstants';
import {
  SPELL_MINION_TYPE,
  POWER_TOUCH_SPELL_ID,
  HEALING_RAIN_SPELL_ID,
  POWER_TOUCH_STAT_BONUS,
  ACID_RAIN_SPELL_ID,
  BLIZZARD_SPELL_ID,
  TRANSFORMED_SPELL_ID,
  DELEVEL_SPELL_ID,
  DIVINE_BLESSING_SPELL_ID,
  INFESTED_SPELL_ID,
  IMPROVED_SPELL_ID,
  UPGRADED_SPELL_ID,
  PROTECTOR_SPELL_ID,
  SACRIFICE_SPELL_ID,
  BAN_HAMMER_SPELL_ID,
  INFESTATION_SPELL_ID,
  TREASURE_HUNTER_SPELL_ID,
  TRANSFORMED_SPELL_TARGET_CLASSES,
  ENRICHMENT_SPELL_ID,
  ENRICHMENT_BASE_GOLD,
  DRAW_SPELL_ID,
  getSpellDefinition,
} from './SpellDefinitions';
import { getBaseDefinitionForClass } from './testMinions';
import {
  ACID_RAIN_ATTACK_COUNT,
  UPGRADED_STAT_BONUS,
  UPGRADED_SPELL_STEP_BONUS,
  DIVINE_BLESSING_ABILITY_ID,
  SWARMLORD_ABILITY_ID,
  TREASURE_HUNTER_ABILITY_ID,
} from './AbilityConstants';
import { DEFAULT_RUNE_LOADOUT, type RuneLoadout } from './RuneDefinitions';

export interface Player {
  id: string;
  life: number;
  gold: number;
  hand: string[]; // Array of minion instance IDs
  battlefield: string[]; // Array of minion instance IDs in order
  isAlive: boolean;
  previousOpponentId: string | null;
  currentRound: number;
  phase: GamePhase;
}

export class PlayerState implements Player {
  public id: string;
  public life: number;
  public gold: number;
  public hand: string[];
  public battlefield: string[];
  public isAlive: boolean;
  public previousOpponentId: string | null;
  public currentRound: number;
  public phase: GamePhase = 'preparation';
  public minionInstances: Map<string, MinionInstance> = new Map();
  /**
   * The player's PREFERRED rune order for this match (rune IDs in the
   * order the player wants to buy them). Set by the match creator from the
   * authoritative rune provider — for BOTS this is their BOT PROFILE's
   * rune build (each profile is a distinct rune identity; the profile
   * engine buys these, in this order, from the in-game rune shop while the
   * profile's buy policy allows). For HUMAN players the shop is fully open
   * (they can buy any rune, in any order) and this field is not consumed
   * by gameplay — it carries the default loadout.
   */
  public runeLoadout: RuneLoadout = [...DEFAULT_RUNE_LOADOUT] as RuneLoadout;
  /**
   * The rune IDs purchased during THIS game, in purchase order. Each rune
   * appears at most once (a rune is buyable ONCE per game — there is no
   * fixed cap on the count, gold is the constraint). These are the
   * player's ACTIVE runes — effects activate immediately after purchase,
   * stack, and persist for the rest of the game. Reset to [] at the start
   * of every match (players begin combat with no active runes).
   */
  public purchasedRunes: string[] = [];
  /**
   * How many combats the Rune of Light has been APPLIED in during this game
   * (0 = never used yet). Incremented by the RoundCoordinator at the start
   * of each combat the rune is active in; the buff magnitude of combat N is
   * N × RUNE_LIGHT_BASE_BONUS (first combat +1/+1, second +2/+2, ...).
   *
   * Per-game by construction: PlayerState instances are created fresh for
   * every match (MatchService.createMatch), so no explicit reset is needed.
   */
  public runeLightUses: number = 0;
  /**
   * The round (1-based) in which this player was ELIMINATED, recorded
   * exactly once by the RoundCoordinator at the moment of death:
   *  - a combat loss / timeout in round N: N, with
   *    eliminatedDuringPreparation = false (recorded by runRound's
   *    elimination check — the round just played);
   *  - a RESIGNATION during the preparation for round N (the death happens
   *    BEFORE that round's combat): N, with eliminatedDuringPreparation =
   *    true (recorded in resignPlayer).
   *
   * Together the two fields form the FINAL PLACEMENT order key
   * (eliminationOrderKey): the player who survived longer is always placed
   * above the one who died earlier — a resignation in the preparation for
   * round N ranks BELOW that round's combat deaths and ABOVE the previous
   * round's. Gold/life never factor in. null = still alive.
   */
  public eliminationRound: number | null = null;
  /**
   * Whether the elimination happened during the PREPARATION phase
   * (resignation) instead of the round's combat. Only meaningful when
   * eliminationRound is set.
   */
  public eliminatedDuringPreparation: boolean = false;
  /**
   * This player's FINAL PLACEMENT (task 199): 1 = the winner (last
   * survivor), N = the first elimination. Assigned exactly once by the
   * server at MATCH END (MatchService) via assignFinalPlacements — the
   * order is PURELY survival-based (the eliminationOrderKey, so it is
   * "how many other players were left alive" when you died) and the
   * TIES (players eliminated in the SAME turn — equal keys) are broken
   * RANDOMLY by the match RNG (the placement among same-turn deaths is
   * random, exactly as the spec requires). null until the match ends.
   */
  public finalPlacement: number | null = null;
  /**
   * This player's battlefield capacity (COOP mode: the humans are capped
   * at 5 minions, the campaign bots at 10). Defaults to
   * GameConfig.maxBoardSize (the standard 7) — the single source every
   * capacity check reads (playMinion / repositionMinions /
   * isBattlefieldFull).
   */
  public boardSizeLimit: number = GameConfig.maxBoardSize;
  /**
   * Healing Rain (spell) — ARMED for the player's NEXT combat: whenever
   * one of the player's minions attacks, every living friendly combat copy
   * restores 2 HP (the RoundCoordinator's applyHealingRain). Set by
   * castSpell (the Healing Rain card) and CONSUMED exactly once, at the
   * start of the combat it applies to (runRound's combat step reads it into
   * the CombatSession and clears it — a player who sits out keeps the
   * charge for the next combat they take part in). Fresh per match by
   * construction (PlayerState instances are created per match).
   */
  public healingRainArmed: boolean = false;
  /**
   * ACID RAIN (spell) — the number of ATTACKS the effect is still active:
   * casting the spell adds ACID_RAIN_ATTACK_COUNT (4); each time the rain
   * triggers (ANY minion attacks while the player's side is armed) it
   * consumes exactly 1 (RoundCoordinator.applyAcidRain — the 4 charges can
   * span several combats; runRound arms the side for a combat while the
   * counter is > 0, without consuming). Fresh per match by construction.
   */
  public acidRainAttacksLeft: number = 0;
  /**
   * UPGRADED (spell, task 183) — how many times THIS player has cast the
   * Upgraded spell (per-player, NOT global). Each cast grants
   * UPGRADED_STAT_BONUS (8) + UPGRADED_SPELL_STEP_BONUS (4) × castsSoFar:
   * 1st cast +8/+8, 2nd +12/+12, 3rd +16/+16, …. Fresh per match by
   * construction.
   */
  public upgradedSpellCasts: number = 0;
  /**
   * BLIZZARD (spell) — ARMED for the player's NEXT combat: before every
   * attack in that combat, the attacking minion has a
   * BLIZZARD_FREEZE_CHANCE (25%) to be FROZEN INSTEAD of attacking
   * (RoundCoordinator.applyBlizzard — the standard freeze-skip timing).
   * Set by castSpell and CONSUMED exactly once, at the start of the combat
   * it applies to (runRound reads it into the CombatSession and clears it
   * — a player who sits out keeps the charge). Fresh per match by
   * construction.
   */
  public blizzardArmed: boolean = false;
  /**
   * BAN HAMMER (spell, task 193) — the minion CLASSES excluded from THIS
   * player's draw pool for the rest of the game. Set by the Ban Hammer
   * cast (the target minion's class) and read by CardDrawService (which
   * filters the random pool per draw; a class with NO remaining pool
   * entry is simply never rolled — when EVERY class is banned the random
   * pool is empty and the draw is SKIPPED: the player "simply doesn't
   * get to draw" — forced draws, e.g. the Necromancer's Undead, are a
   * specific card and are never filtered). Per-CASTER by construction:
   * every player has its own list (a COOP teammate's bans never apply to
   * the other member, and no opponent is ever affected). Fresh per match
   * by construction (new PlayerState per match).
   */
  public bannedMinionTypes: string[] = [];
  /**
   * INFESTATION (spell, task 194) — ARMED for the player's NEXT combat:
   * whenever ANY minion dies in that combat (on EITHER side), a Fragile
   * Swarmer (a SWARMER token — task 204) replaces the dead minion in place
   * and attacks immediately (resolved after everything else, only if there
   * is space, token deaths don't chain — the RoundCoordinator's
   * applyInfestation). Set by castSpell and
   * CONSUMED exactly once, at the start of the combat it applies to (the
   * healingRainArmed/blizzardArmed convention — a player who sits out
   * keeps the charge). Fresh per match by construction.
   */
  public infestationArmed: boolean = false;

  /**
   * Bans a minion class from this player's draw pool (Ban Hammer —
   * task 193). Idempotent (casting on two mages bans 'mage' once).
   */
  public banMinionType(minionType: string): void {
    if (!this.bannedMinionTypes.includes(minionType)) {
      this.bannedMinionTypes.push(minionType);
    }
  }

  constructor(id: string) {
    this.id = id;
    this.life = GameConfig.startingLife;
    this.gold = GameConfig.startingGold;
    this.hand = [];
    this.battlefield = [];
    this.isAlive = true;
    this.previousOpponentId = null;
    this.currentRound = 1;
    this.phase = 'preparation';
  }

  /**
   * True when the given rune ID is one of this player's ACTIVE (purchased)
   * runes. Single lookup point for the combat rune effects.
   */
  public hasActiveRune(runeId: string): boolean {
    return this.purchasedRunes.includes(runeId);
  }

  public addGold(amount: number): void {
    this.gold += amount;
  }

  public subtractGold(amount: number): boolean {
    if (this.gold >= amount) {
      this.gold -= amount;
      return true;
    }
    return false;
  }

  public modifyLife(amount: number): void {
    this.life += amount;
    if (this.life <= 0) {
      this.life = 0;
      this.isAlive = false;
      this.phase = 'eliminated';
    }
  }

  public updateRound(round: number): void {
    this.currentRound = round;
  }

  /**
   * Sets the current game phase for this player.
   * @param phase The phase to set.
   */
  public setPhase(phase: GamePhase): void {
    this.phase = phase;
  }

  /**
   * Returns true if the player is currently in the preparation phase
   * and can perform preparation actions (play, sell, reposition, upgrade).
   */
  public isInPreparationPhase(): boolean {
    return this.phase === 'preparation';
  }

  public addCard(minion: MinionInstance): boolean {
    if (this.hand.length < GameConfig.maxHandSize) {
      this.minionInstances.set(minion.instanceId, minion);
      this.hand.push(minion.instanceId);
      return true;
    }
    return false;
  }

  /**
   * Returns the current number of minions on the battlefield.
   */
  public getBattlefieldSize(): number {
    return this.battlefield.length;
  }

  /**
   * Grandmaster: the number of Grandmaster minions currently ON this
   * player's battlefield (cards in the hand do NOT count — the ability
   * only works "while this minion is on your board").
   */
  public countGrandmastersOnBattlefield(): number {
    let count = 0;
    for (const instanceId of this.battlefield) {
      const minion = this.minionInstances.get(instanceId);
      if (minion && minion.abilities.includes(GRANDMASTER_ABILITY_ID)) {
        count += 1;
      }
    }
    return count;
  }

  /**
   * Spellmaker / SpellMaster: the owner's SPELL DRAW POWER from minions
   * currently ON this player's battlefield (cards in the hand do NOT count
   * — the keyword only works "while on your battlefield"): 2×SpellMasters
   * + 1×Spellmakers (the effects STACK). The draw services add +1 for the
   * Rune of the Mage on top of this (the rune is not a minion, so it is
   * not counted here). See AbilityConstants for the full rule.
   */
  public countSpellPowerOnBattlefield(): number {
    let power = 0;
    for (const instanceId of this.battlefield) {
      const minion = this.minionInstances.get(instanceId);
      if (!minion) continue;
      if (minion.abilities.includes(SPELLMAKER_ABILITY_ID)) {
        power += SPELLMAKER_SPELL_POWER;
      }
      if (minion.abilities.includes(SPELLMASTER_ABILITY_ID)) {
        power += SPELLMASTER_SPELL_POWER;
      }
    }
    return power;
  }

  /**
   * The effective maximum upgrade count for this player's minions:
   * GameConfig.maxUpgrades (or an explicit base, e.g. a test's config
   * override) plus ONE per Grandmaster currently on the battlefield
   * (the effects STACK — two Grandmasters allow two extra levels).
   *
   * This is the single shared source for the level cap, used by every
   * cap site (the authoritative PlayerState.upgradeMinion check, the
   * GameSessionService pre-check, the bot upgrade logic, and the
   * frontend UpgradeChoiceUI max-level mirror). Levels already earned
   * are never taken away — this only gates FUTURE upgrades, so minions
   * that leveled up while a Grandmaster was on the board keep their
   * level when it leaves.
   */
  public getMaxUpgrades(baseMaxUpgrades: number = GameConfig.maxUpgrades): number {
    return baseMaxUpgrades + this.countGrandmastersOnBattlefield();
  }

  /**
   * Returns true if the battlefield has reached its maximum capacity
   * (the per-player boardSizeLimit — GameConfig.maxBoardSize by default;
   * COOP humans are capped at 5, campaign bots at 10).
   */
  public isBattlefieldFull(): boolean {
    return this.battlefield.length >= this.boardSizeLimit;
  }

  /**
   * Plays a minion from hand to the battlefield for 0 gold.
   * The minion is placed at the specified position (or at the end if not specified).
   * Only allowed during the preparation phase.
   * @param instanceId The instance ID of the minion to play.
   * @param targetPosition Optional: the battlefield position (0-indexed) to place the minion.
   *                       Existing minions at that position and beyond are shifted right.
   *                       If omitted or out of range, the minion is placed at the end.
   * @returns true if the minion was successfully played, false otherwise.
   */
  public playMinion(instanceId: string, targetPosition?: number): boolean {
    // Validate the player is in preparation phase
    if (!this.isInPreparationPhase()) {
      return false;
    }

    // Validate the minion exists in hand
    const handIndex = this.hand.indexOf(instanceId);
    if (handIndex === -1) {
      return false;
    }

    // Spells are CAST (castSpell), never played — they never land on the
    // battlefield (the spell's effect goes into play and the card despawns).
    const handMinion = this.minionInstances.get(instanceId);
    if (handMinion && handMinion.minionType === SPELL_MINION_TYPE) {
      return false;
    }

    // Validate battlefield has capacity
    if (this.isBattlefieldFull()) {
      return false;
    }

    // Remove from hand
    this.hand.splice(handIndex, 1);

    // Determine the actual position to insert at
    const actualPosition = (typeof targetPosition === 'number' &&
                            targetPosition >= 0 &&
                            targetPosition <= this.battlefield.length)
      ? targetPosition
      : this.battlefield.length; // Default: append at end

    // Insert at the target position (shift existing minions right)
    this.battlefield.splice(actualPosition, 0, instanceId);

    // Update the new minion's battlefield position
    const minion = this.minionInstances.get(instanceId);
    if (minion) {
      minion.setBattlefieldPosition(actualPosition);
    }

    // Shift positions of minions that were moved right
    for (let i = actualPosition + 1; i < this.battlefield.length; i++) {
      const shiftedMinion = this.minionInstances.get(this.battlefield[i]);
      if (shiftedMinion) {
        shiftedMinion.setBattlefieldPosition(i);
      }
    }

    return true;
  }

  /**
   * Repositions minions on the battlefield to a new order.
   * All minion instances and their stats are preserved; only their positions change.
   * Only allowed during the preparation phase.
   * @param newOrder An array of minion instance IDs representing the desired battlefield order.
   * @returns true if repositioning succeeded, false if the new order is invalid.
   */
  public repositionMinions(newOrder: string[]): boolean {
    // Validate the player is in preparation phase
    if (!this.isInPreparationPhase()) {
      return false;
    }

    // Must have the same number of minions
    if (newOrder.length !== this.battlefield.length) {
      return false;
    }

    // Must not exceed the per-player board size limit
    if (newOrder.length > this.boardSizeLimit) {
      return false;
    }

    // Every ID in newOrder must exist on the current battlefield
    const battlefieldSet = new Set(this.battlefield);
    const newOrderSet = new Set(newOrder);

    // Same size + every newOrder element in battlefield = exact permutation
    if (newOrderSet.size !== battlefieldSet.size) {
      return false;
    }
    for (const id of newOrder) {
      if (!battlefieldSet.has(id)) {
        return false;
      }
    }

    // Apply the new order
    this.battlefield = [...newOrder];

    // Update each minion's battlefield position
    newOrder.forEach((instanceId, index) => {
      const minion = this.minionInstances.get(instanceId);
      if (minion) {
        minion.setBattlefieldPosition(index);
      }
    });

    return true;
  }

  /**
   * Sells a minion, removing it permanently. The minion may be either on
   * the battlefield OR in the hand. Awards gold equal to the minion's level
   * (1 + upgradeCount) in both cases.
   *
   * Selling is a REMOVAL, not a play: it never triggers play-related effects.
   * In particular, selling an Undead card does NOT count as playing it, so
   * the match-wide Improved played/summoned counter (owned by the
   * RoundCoordinator) must not be incremented for sales.
   *
   * Only allowed during the preparation phase.
   * @param instanceId The instance ID of the minion to sell.
   * @returns true if the minion was successfully sold, false otherwise.
   */
  public sellMinion(instanceId: string): boolean {
    // Validate the player is in preparation phase
    if (!this.isInPreparationPhase()) {
      return false;
    }

    // Get the minion and validate it is on the battlefield or in the hand
    const minion = this.minionInstances.get(instanceId);
    if (!minion) {
      return false;
    }

    const battlefieldIndex = this.battlefield.indexOf(instanceId);
    const handIndex = this.hand.indexOf(instanceId);
    if (battlefieldIndex === -1 && handIndex === -1) {
      return false;
    }

    // Spells sell for their own fixed gold value (SpellDefinitions
    // `sellValue`) — they are always level-1 cards, so a level-based value
    // would always be 1. Every other card sells for its level.
    const goldAwarded = minion.minionType === SPELL_MINION_TYPE
      ? (getSpellDefinition(minion.baseMinionId)?.sellValue ?? 0)
      : minion.getLevel();

    if (battlefieldIndex !== -1) {
      // Remove from battlefield
      this.battlefield.splice(battlefieldIndex, 1);

      // Clear the minion's battlefield position
      minion.clearBattlefieldPosition();

      // Re-index remaining minions' battlefield positions
      this.battlefield.forEach((id, index) => {
        const remainingMinion = this.minionInstances.get(id);
        if (remainingMinion) {
          remainingMinion.setBattlefieldPosition(index);
        }
      });
    } else {
      // Remove from hand
      this.hand.splice(handIndex, 1);
    }

    // Award gold
    this.addGold(goldAwarded);

    // Remove the minion instance permanently
    this.minionInstances.delete(instanceId);

    return true;
  }

  /**
   * Gives a minion to the COOP teammate: the minion MOVES from this
   * player (hand or battlefield) into the teammate's HAND, keeping the
   * SAME instance — all keywords, enchantments, level and current stats
   * travel with it (the MinionInstance itself transfers between the two
   * players' instance maps).
   *
   * Rules (the authoritative caller, GameSessionService.giveMinion, has
   * already validated gold/phase/ownership/teammate hand capacity — this
   * is the single mutation point):
   *  - the minion must be in this player's hand or on the battlefield;
   *  - the minion is removed from the owner (hand slot freed / battlefield
   *    re-indexed, the sell convention) and placed at the END of the
   *    teammate's hand (hand capacity must have been validated);
   *  - it is a transfer, NOT a play: no play effects, no Improved counter
   *    change (the same convention as a sell).
   *
   * @param teammate The teammate's PlayerState.
   * @param instanceId The instance ID of the minion to give away.
   * @returns true when the minion was transferred; false (nothing
   *   mutated) when the minion is not owned or the teammate's hand is full.
   */
  public giveMinionTo(teammate: PlayerState, instanceId: string): boolean {
    const minion = this.minionInstances.get(instanceId);
    if (!minion) return false;

    const battlefieldIndex = this.battlefield.indexOf(instanceId);
    const handIndex = this.hand.indexOf(instanceId);
    if (battlefieldIndex === -1 && handIndex === -1) return false;

    // The teammate's hand must have a free slot (validated by the caller;
    // re-checked here — the model is its own backstop).
    if (teammate.hand.length >= GameConfig.maxHandSize) return false;

    // Remove from the owner (the sell conventions — re-index the
    // battlefield, free the hand slot).
    if (battlefieldIndex !== -1) {
      this.battlefield.splice(battlefieldIndex, 1);
      minion.clearBattlefieldPosition();
      this.battlefield.forEach((id, index) => {
        const remaining = this.minionInstances.get(id);
        if (remaining) remaining.setBattlefieldPosition(index);
      });
    } else {
      this.hand.splice(handIndex, 1);
    }

    // Transfer the instance to the teammate (same object — every keyword,
    // enchantment, level and stat is preserved by construction).
    this.minionInstances.delete(instanceId);
    teammate.minionInstances.set(instanceId, minion);
    teammate.hand.push(instanceId);

    return true;
  }

  /**
   * CASTS a spell from the hand: the spell's effect goes into play
   * immediately and the card DESPAWNS (removed from the hand AND the
   * instance map — the sell conventions minus the gold, which is not
   * awarded: casting is free). The spell NEVER lands on the battlefield.
   *
   * Per-spell effects (the spell's spec — shared SpellDefinitions):
   *  - 'minion' target (Power touch): `targetInstanceId` must be a friendly
   *    minion on the battlefield; it permanently gains
   *    POWER_TOUCH_STAT_BONUS attack AND HP (gainHp — the Grandmaster cap
   *    applies). Like every PERMANENT stat gain, a Teacher target redirects
   *    the gain to its neighbors (redirectTeacherStatGain — the Teacher
   *    intercepts convention).
   *  - 'battlefield' target (Healing Rain): arms `healingRainArmed` — the
   *    player's next combat restores 2 HP to every friendly minion per
   *    friendly attack (consumed by the RoundCoordinator there).
   *  - New spells (Task: New Spells):
   *    - Acid Rain (battlefield): adds ACID_RAIN_ATTACK_COUNT (4) attacks
   *      to `acidRainAttacksLeft` (the rain triggers on the next 4 attacks
   *      — each dealing 1 damage to ALL minions; the charges can span
   *      combats — RoundCoordinator.applyAcidRain).
   *    - Blizzard (battlefield): arms `blizzardArmed` — the next combat's
   *      attacks have a 25% freeze chance (RoundCoordinator.applyBlizzard).
   *    - Improved (battlefield): NO player-local effect — the authoritative
   *      caller (GameSessionService.playSpell) advances the match-wide
   *      Improved counter by 2 (the registerUndeadPlayed convention); this
   *      method only despawns the card.
   *    - Treasure Hunter (minion): grants the TREASURE HUNTER keyword (the
   *      first kill spawns a treasure on the enemy board — the
   *      RoundCoordinator's Phase-3 detection + post-combat spawn).
   *    - Divine Blessing (minion): grants the DIVINE BLESSING keyword (the
   *      first damage taken in combat is ignored — CombatMinionCopy.
   *      takeDamage).
   *    - Infested (minion): grants the SWARMLORD keyword IMMEDIATELY
   *      (temporary, marked with infestedSwarmLord — it lasts ONE combat
   *      only; the RoundCoordinator records the holder at session creation
   *      and the post-combat step removes the keyword — the minion stays).
   *    - Transformed (minion): the minion BECOMES a random other class
   *      (TRANSFORMED_SPELL_TARGET_CLASSES minus its own class — chosen by
   *      `opts.rng`, REQUIRED for this spell: the authoritative caller
   *      passes the match's seeded RNG for deterministic runs), keeping its
   *      level and current stats; it loses ALL keywords (abilities = []).
   *    - Delevel (minion): removes 1 level (upgradeCount - 1, floored at 0)
   *      WITHOUT touching stats or keywords.
   *    - Upgraded (minion): permanently +UPGRADED_STAT_BONUS/+UPGRADED_STAT_BONUS
   *      (the Power Touch convention: gainHp clamps at the Grandmaster cap,
   *      a Teacher target redirects the gain).
   *
   * Only allowed during the preparation phase. Atomic: nothing is mutated
   * when the cast is refused.
   *
   * @param instanceId The instance ID of the spell card in the hand.
   * @param targetInstanceId The target for 'minion'-target spells (a
   *   friendly battlefield minion). Ignored for 'battlefield' spells.
   * @param opts Optional cast options. `rng` (seeded match RNG) is REQUIRED
   *   by the Transformed spell (the random class roll); every other spell
   *   ignores it. `ignorePhase` lets an AUTHORITATIVE in-combat caller cast
   *   (the Rune of Knowledge fires at combat start, when the phase is
   *   'combat') — the client path never sets it.
   * @returns true when the spell was cast; false (nothing mutated) when the
   *   cast is invalid (wrong phase, not in hand, not a spell, unknown
   *   spell, missing/invalid target, missing RNG for Transformed).
   */
  /**
   * Applies a MINION-TARGETING spell's effect to the target (the shared
   * effect core — castSpell and the Repeater ability both go through
   * here, so the Repeater's re-cast is byte-for-byte the spell's own
   * effect: permanent stats/keywords/markers/counters, the Teacher
   * redirect, the Grandmaster HP cap, the Upgraded per-player
   * escalation). Returns false for a spell this method does not apply
   * (unknown id / a Transformed without an RNG).
   */
  public applyMinionSpellEffect(
    spec: { id: string },
    target: MinionInstance,
    opts: { rng?: () => number } = {},
  ): boolean {
    switch (spec.id) {
case POWER_TOUCH_SPELL_ID:
      case UPGRADED_SPELL_ID: {
        // PERMANENT +N/+N (gainHp clamps at the Grandmaster cap; a
        // Grandmaster target keeps the attack, gains no HP — the Master
        // Buff convention), then the Teacher redirect (permanent stat
        // gains are intercepted by a Teacher target, like
        // upgrades/Heirloom). POWER_TOUCH_STAT_BONUS (5) for Power touch.
        // UPGRADED (task 183) escalates PER PLAYER: +8/+8 the first cast,
        // +12/+12 the second, +16/+16 the third … (8 + 4×castsSoFar —
        // the counter increments only on a successful cast).
        const bonus = spec.id === UPGRADED_SPELL_ID
          ? UPGRADED_STAT_BONUS + UPGRADED_SPELL_STEP_BONUS * this.upgradedSpellCasts
          : POWER_TOUCH_STAT_BONUS;
        target.attack += bonus;
        target.gainHp(bonus);
        if (spec.id === UPGRADED_SPELL_ID) this.upgradedSpellCasts += 1;
        this.redirectTeacherStatGain(target, bonus, bonus);
        break;
      }
      case TRANSFORMED_SPELL_ID: {
        // Random class (NOT the same, NOT a Swarmer — the shared
        // candidate list already excludes swarmer/spell/item), chosen by
        // the seeded RNG (REQUIRED — a deterministic per-match roll).
        // The minion loses ALL levels too: it is RESET to a LEVEL-1 minion
        // of the new class (the class's BASE stats + BASE abilities — the
        // level-1 identity, like the transform system's fresh minion), so
        // its upgradeCount drops to 0 and its hp/atk become the new class's
        // base values. It loses ALL gained keywords (base keywords of the
        // new class are its own level-1 kit).
        if (!opts.rng) return false;
        const candidates = TRANSFORMED_SPELL_TARGET_CLASSES.filter(
          (t) => t !== target.minionType,
        );
        if (candidates.length === 0) return false;
        const roll = Math.floor(opts.rng() * candidates.length);
        const newType = candidates[roll];
        const baseDef = getBaseDefinitionForClass(newType);
        if (!baseDef) return false;
        target.minionType = newType;
        target.upgradeCount = 0;
        target.currentHp = baseDef.startingHp;
        target.attack = baseDef.attack;
        target.abilities = [...baseDef.baseAbilities];
        target.upgradePathState = 'base';
        break;
      }
      case DELEVEL_SPELL_ID: {
        // Remove 1 level WITHOUT removing stats or keywords (floored at
        // level 1 — a level-1 minion simply stays level 1).
        target.upgradeCount = Math.max(0, target.upgradeCount - 1);
        break;
      }
      case DIVINE_BLESSING_SPELL_ID: {
        // Grant the DIVINE BLESSING keyword (first damage taken in combat
        // is ignored — the effect lives in CombatMinionCopy.takeDamage).
        if (!target.abilities.includes(DIVINE_BLESSING_ABILITY_ID)) {
          target.abilities.push(DIVINE_BLESSING_ABILITY_ID);
        }
        break;
      }
      case INFESTED_SPELL_ID: {
        // Grant the SWARMLORD keyword IMMEDIATELY (the minion shows it on
        // its card from the cast on) — it lasts ONE combat only: the
        // RoundCoordinator records the holder at session creation and the
        // post-combat step removes the keyword (the minion stays). A
        // minion that already carries a (class-granted) SwarmLord keeps
        // its permanent one — the spell is a no-op for it (no marker).
        if (!target.abilities.includes(SWARMLORD_ABILITY_ID)) {
          target.abilities.push(SWARMLORD_ABILITY_ID);
          target.infestedSwarmLord = true;
        }
        break;
      }
      case TREASURE_HUNTER_SPELL_ID: {
        // Grant the TREASURE HUNTER keyword (task 196 — ONE COMBAT ONLY:
        // during the next combat the first kill replaces the corpse in
        // combat with a temporary treasure; the RoundCoordinator records
        // the holder at session creation and the post-combat step removes
        // the keyword — the minion stays).
        if (!target.abilities.includes(TREASURE_HUNTER_ABILITY_ID)) {
          target.abilities.push(TREASURE_HUNTER_ABILITY_ID);
        }
        break;
      }
      case PROTECTOR_SPELL_ID: {
        // Grant the SHIELD keyword PERMANENTLY (task 183 — taunt: enemies
        // must attack the target while it lives; CombatService's
        // selectTargetWithShieldPriority reads the 'shield' ability). A
        // minion that already carries a (class-granted) Shield is
        // unchanged — the spell is a no-op for it (the card is consumed).
        if (!target.abilities.includes('shield')) {
          target.abilities.push('shield');
        }
        break;
      }
      case BAN_HAMMER_SPELL_ID: {
        // BAN the target's CLASS from the CASTER'S draw pool for the rest
        // of the game (task 193 — see bannedMinionTypes): the target
        // minion itself is untouched (no stats/keywords/death), only the
        // caster's future random draws lose that class. CardDrawService
        // applies the filter per draw; other players keep drawing it.
        this.banMinionType(target.minionType);
        break;
      }
      case SACRIFICE_SPELL_ID: {
        // DESTROY the target (task 186 — no gold, no compensation, no death
        // effects: the Rune of High Stakes destruction convention minus the
        // cost): remove from the battlefield + the instance map and
        // re-index the remaining minions' positions, exactly like a sell.
        // The caster's card draws (one per target level) are handled by the
        // AUTHORITATIVE CALLER — the model has no draw service. castSpell's
        // minion-target branch validated the target is on this battlefield,
        // so the index is always found here.
        const index = this.battlefield.indexOf(target.instanceId);
        this.battlefield.splice(index, 1);
        target.clearBattlefieldPosition();
        this.battlefield.forEach((id, i) => {
          const remaining = this.minionInstances.get(id);
          if (remaining) remaining.setBattlefieldPosition(i);
        });
        this.minionInstances.delete(target.instanceId);
        break;
      }

        default:
          return false;
      }
      // REPEATER tracking (task 184): record the last minion-targeting
      // spell cast ON this minion (Repeater re-casts it at combat start)
      // — the record is set in the SHARED core so the Repeater's own
      // re-casts update the neighbor's record too.
      target.lastCastSpellId = spec.id;
      return true; // every case above breaks here (the spell was applied)
  }

  public castSpell(
    instanceId: string,
    targetInstanceId?: string,
    opts: { rng?: () => number; ignorePhase?: boolean } = {},
  ): boolean {
    // Validate the player is in preparation phase (authoritative in-combat
    // callers — the Rune of Knowledge — bypass it via opts.ignorePhase).
    if (!opts.ignorePhase && !this.isInPreparationPhase()) {
      return false;
    }

    // Validate the card exists in hand and is a spell.
    const handIndex = this.hand.indexOf(instanceId);
    if (handIndex === -1) {
      return false;
    }
    const spell = this.minionInstances.get(instanceId);
    if (!spell || spell.minionType !== SPELL_MINION_TYPE) {
      return false;
    }
    const spec = getSpellDefinition(spell.baseMinionId);
    if (!spec) {
      return false;
    }

    if (spec.target === 'minion') {
      // Targeted: the target must be a friendly minion ON the battlefield.
      if (!targetInstanceId) {
        return false;
      }
      if (!this.battlefield.includes(targetInstanceId)) {
        return false;
      }
      const target = this.minionInstances.get(targetInstanceId);
      if (!target) {
        return false;
      }
      if (!this.applyMinionSpellEffect(spec, target, opts)) return false;
    } else {
      // Battlefield spells: arm the next-combat charges.
      switch (spec.id) {
        case HEALING_RAIN_SPELL_ID:
          this.healingRainArmed = true;
          break;
        case ACID_RAIN_SPELL_ID:
          this.acidRainAttacksLeft += ACID_RAIN_ATTACK_COUNT;
          break;
        case BLIZZARD_SPELL_ID:
          this.blizzardArmed = true;
          break;
        case INFESTATION_SPELL_ID:
          // Arms the NEXT combat (task 194 — see infestationArmed): every
          // death in it spawns a Fragile Swarmer token on the dead minion's
          // side.
          this.infestationArmed = true;
          break;
        case ENRICHMENT_SPELL_ID:
          // GOLD NOW (task 197): 1 gold + 1 per turn the card sat in the
          // hand (the per-card counter — ENRICHMENT_BASE_GOLD +
          // enrichmentTurns). The caster's own gold (like every economy
          // effect — the coop team gets no share). The card is consumed
          // below (the counter dies with it).
          this.gold += ENRICHMENT_BASE_GOLD + spell.enrichmentTurns;
          break;
        case IMPROVED_SPELL_ID:
          // No player-local effect: the authoritative caller advances the
          // match-wide Improved counter (registerUndeadPlayed × 2).
          break;
        case DRAW_SPELL_ID:
          // DRAW (task 219): no player-local state — the 2 immediate card
          // draws are performed by the AUTHORITATIVE CALLER (the model has
          // no draw service): GameSessionService.playSpell and the
          // RoundCoordinator's Rune of Knowledge cast (the SACRIFICE
          // convention — each draw through the shared overdraw hook, so a
          // full hand loses the card with the red-card explosion + the
          // Sacrificial Rune trigger).
          break;
        default:
          return false;
      }
    }

    // Despawn: consume the card (hand slot freed, instance deleted).
    this.hand.splice(handIndex, 1);
    this.minionInstances.delete(instanceId);

    return true;
  }

  /**
   * Calculates the effective upgrade cost for a minion.
   * Base formula: GameConfig.upgradeCost + minion.upgradeCount
   * (e.g., level 1→2 costs 3, level 2→3 costs 4, level 3→4 costs 5, etc.)
   * An optional modifier function can further transform the cost.
   * @param minionUpgradeCount The minion's current upgrade count (0 for level 1).
   * @param modifier An optional function that transforms the computed cost.
   * @returns The effective upgrade cost.
   */
  public calculateUpgradeCost(minionUpgradeCount: number, modifier?: (cost: number) => number): number {
    let effectiveCost = GameConfig.upgradeCost + minionUpgradeCount;
    if (modifier) {
      effectiveCost = modifier(effectiveCost);
    }
    return effectiveCost;
  }

  /**
   * Purchases and applies an upgrade to a battlefield minion.
   * Cost is calculated through the modifier pipeline.
   * Only allowed during the preparation phase.
   * @param instanceId The instance ID of the minion to upgrade.
   * @param branch The upgrade branch to apply.
   * @param costModifier An optional function that transforms the base upgrade cost.
   * @returns true if the upgrade was successfully purchased and applied, false otherwise.
   */
  public upgradeMinion(instanceId: string, branch: UpgradeBranch, costModifier?: (cost: number) => number): boolean {
    // Validate the player is in preparation phase
    if (!this.isInPreparationPhase()) {
      return false;
    }

    // Validate the minion is on the battlefield
    if (!this.battlefield.includes(instanceId)) {
      return false;
    }

    // Get the minion instance
    const minion = this.minionInstances.get(instanceId);
    if (!minion) {
      return false;
    }

    // Enforce maximum upgrade limit (extended while Grandmasters are on
    // the battlefield — see getMaxUpgrades).
    if (minion.upgradeCount >= this.getMaxUpgrades()) {
      return false;
    }

    // Calculate effective cost: base (3) + upgradeCount, then apply modifier
    const effectiveCost = this.calculateUpgradeCost(minion.upgradeCount, costModifier);

    // Check sufficient gold
    if (this.gold < effectiveCost) {
      return false;
    }

    // Deduct gold
    this.subtractGold(effectiveCost);

    // Capture pre-upgrade Fortress state: a newly ACQUIRED Fortress adds
    // FORTRESS_HP_BONUS to this upgrade's permanent stat gain (applied
    // inside applyUpgrade), so the Teacher redirect below must include it.
    const hadFortress = minion.abilities.includes(FORTRESS_ABILITY_ID);

    // Apply the upgrade
    minion.applyUpgrade(branch);

    // Teacher: the permanent STAT gains this upgrade granted are redirected
    // to the Teacher's battlefield neighbors (each receives the full amount,
    // the Teacher itself receives none). Gained keywords/abilities are NOT
    // redirected — they stay with the Teacher. No-op unless the minion has
    // Teacher (e.g., a minion acquiring Teacher in this very upgrade still
    // redirects, matching the Fortress "acquired" convention).
    const fortressGain = (branch.abilitiesAdded?.includes(FORTRESS_ABILITY_ID) ?? false) && !hadFortress
      ? FORTRESS_HP_BONUS
      : 0;
    this.redirectTeacherStatGain(minion, branch.hpChange + fortressGain, branch.attackChange);

    return true;
  }

  /**
   * Transforms a battlefield minion into ANOTHER class (the transform
   * system, TransformDefinitions): the original minion is REMOVED (from the
   * battlefield AND the instance map) and replaced with a fresh LEVEL-1
   * minion of `targetDefinition`'s class at the SAME battlefield position
   * (base stats + base abilities of the target definition).
   *
   * Costs the SAME as the upgrade that offers it: the transform is one of
   * the upgrade options, so the cost is calculated through the same
   * modifier pipeline as an upgrade of this minion (base + upgradeCount).
   * The max-upgrade cap is NOT checked here: the transform's own gate is
   * the shared spec (source class + EXACT level), which the caller
   * (GameSessionService, authoritative) validates before calling this.
   *
   * `newInstanceId` is generated by the CALLER with the match's seeded RNG
   * (deterministic runs stay deterministic — the CardDrawService id
   * convention); it must be unique to the match.
   *
   * Only allowed during the preparation phase.
   *
   * @param instanceId The instance ID of the minion to transform.
   * @param targetDefinition The target class's card definition.
   * @param newInstanceId The fresh instance ID for the replacement minion.
   * @param costModifier An optional function that transforms the base upgrade cost.
   * @returns The NEW minion instance, or null when the transform cannot be
   *   performed (wrong phase, minion not on the battlefield, unknown ID or
   *   not enough gold). Nothing is mutated on failure.
   */
  public transformMinion(
    instanceId: string,
    targetDefinition: MinionDefinition,
    newInstanceId: string,
    costModifier?: (cost: number) => number
  ): MinionInstance | null {
    // Validate the player is in preparation phase
    if (!this.isInPreparationPhase()) {
      return null;
    }

    // Validate the minion is on the battlefield (position = array index)
    const index = this.battlefield.indexOf(instanceId);
    if (index === -1) {
      return null;
    }

    const oldMinion = this.minionInstances.get(instanceId);
    if (!oldMinion) {
      return null;
    }

    // Cost: the same upgrade cost the option was offered at (base + the
    // minion's current upgradeCount), through the modifier pipeline.
    const effectiveCost = this.calculateUpgradeCost(oldMinion.upgradeCount, costModifier);
    if (this.gold < effectiveCost) {
      return null;
    }

    // Deduct gold, then remove the original minion.
    this.subtractGold(effectiveCost);
    this.battlefield.splice(index, 1);
    this.minionInstances.delete(instanceId);
    oldMinion.clearBattlefieldPosition();

    // ...and replace it with a fresh LEVEL-1 minion of the target class at
    // the same position (base stats + base abilities of the definition).
    const newMinion = new MinionInstance(
      newInstanceId,
      targetDefinition.id,
      targetDefinition.startingHp,
      targetDefinition.attack,
      0, // level 1
      'base',
      targetDefinition.baseAbilities,
      targetDefinition.minionType
    );
    this.battlefield.splice(index, 0, newMinion.instanceId);
    this.minionInstances.set(newMinion.instanceId, newMinion);
    newMinion.setBattlefieldPosition(index);

    return newMinion;
  }

  /**
   * Destroys a battlefield minion WITHOUT ANY compensation (the Rune of
   * High Stakes failure roll): the player PAYS the same upgrade cost an
   * upgrade of this minion would cost (base + upgradeCount, through the
   * same modifier pipeline), the minion is REMOVED from the battlefield
   * AND the instance map, and NO gold is awarded (unlike a sell — this is
   * a pure loss). The remaining minions' battlefield positions are
   * re-indexed, like a sell.
   *
   * Only allowed during the preparation phase (the same gate as upgrades
   * — the authoritative caller, GameSessionService.upgradeMinion, has
   * already run the full validation; this is the single mutation point).
   *
   * @param instanceId The instance ID of the minion to destroy.
   * @param costModifier An optional function that transforms the base upgrade cost.
   * @returns true when the minion was destroyed; false (nothing mutated)
   *   when the player is not in preparation, the minion is not on the
   *   battlefield, or the player cannot pay the cost.
   */
  public destroyBattlefieldMinion(
    instanceId: string,
    costModifier?: (cost: number) => number
  ): boolean {
    // Validate the player is in preparation phase
    if (!this.isInPreparationPhase()) {
      return false;
    }

    // Validate the minion is on the battlefield (position = array index)
    const index = this.battlefield.indexOf(instanceId);
    if (index === -1) {
      return false;
    }

    const minion = this.minionInstances.get(instanceId);
    if (!minion) {
      return false;
    }

    // Cost: the same upgrade cost the option was offered at (base + the
    // minion's current upgradeCount), through the modifier pipeline.
    const effectiveCost = this.calculateUpgradeCost(minion.upgradeCount, costModifier);
    if (this.gold < effectiveCost) {
      return false;
    }

    // Deduct the cost — and remove the minion. NO gold awarded: the High
    // Stakes destruction is a loss, not a sell.
    this.subtractGold(effectiveCost);
    this.battlefield.splice(index, 1);
    minion.clearBattlefieldPosition();

    // Re-index the remaining minions' battlefield positions (the sell
    // convention — positions are array indices).
    this.battlefield.forEach((id, i) => {
      const remaining = this.minionInstances.get(id);
      if (remaining) {
        remaining.setBattlefieldPosition(i);
      }
    });
    this.minionInstances.delete(instanceId);

    return true;
  }

  /**
   * Resolves (WITHOUT mutating anything) which minions ultimately receive a
   * permanent stat gain that a Teacher minion would have received.
   *
   * Returns [] when no redirect happens (owner lacks Teacher, is not on the
   * battlefield, or the gain is zero) — the gain owner then keeps the gain.
   * Otherwise returns the final recipients of the FULL amount:
   *  - every non-Teacher unit that receives it, plus
   *  - the gain owner itself when the cascade ends at a Teacher that has no
   *    other neighbor to pass to (keeps it for itself).
   *
   * Deterministic (no RNG): the same board + owner always yields the same
   * recipients. This is the single source of truth for the Teacher cascade,
   * shared by:
   *  - redirectTeacherStatGain — applies the redistribution to the persistent
   *    model (upgrades, combat permanent buffs, Heirloom transfers), and
   *  - buffVitalityExecutor — applies the IN-COMBAT portion to the combat
   *    copies, so a Teacher never shows the buff during the combat itself
   *    (positions are fixed for the duration of combat, so both resolutions
   *    always agree).
   */
  public resolveTeacherStatRedistribution(
    gainOwner: MinionInstance,
    hpGain: number,
    atkGain: number
  ): Array<{ instanceId: string; hpGain: number; atkGain: number }> {
    if (hpGain === 0 && atkGain === 0) return [];
    if (!gainOwner.abilities.includes(TEACHER_ABILITY_ID)) return [];

    if (this.battlefield.indexOf(gainOwner.instanceId) === -1) return []; // Effect is battlefield-only

    // Cascade the gain outward. Each step never sends the gain back to the
    // unit it came from, so lines of Teachers cannot loop.
    const queue: Array<{ unitId: string; sourceId: string | null }> = [
      { unitId: gainOwner.instanceId, sourceId: null },
    ];
    const visited = new Set<string>([gainOwner.instanceId]);
    const recipients: Array<{ instanceId: string; hpGain: number; atkGain: number }> = [];

    while (queue.length > 0) {
      const step = queue.shift()!;
      const unit = this.minionInstances.get(step.unitId);
      if (!unit) continue;

      const unitIndex = this.battlefield.indexOf(step.unitId);
      const neighborIds = (unitIndex === -1 ? [] : [
        this.battlefield[unitIndex - 1],
        this.battlefield[unitIndex + 1],
      ]).filter((id): id is string => typeof id === 'string' && id !== step.sourceId);

      if (neighborIds.length === 0) {
        // Nowhere to pass the gain — the unit keeps it for itself.
        recipients.push({ instanceId: step.unitId, hpGain, atkGain });
        continue;
      }

      for (const neighborId of neighborIds) {
        const neighbor = this.minionInstances.get(neighborId);
        if (!neighbor || visited.has(neighborId)) continue;
        visited.add(neighborId);

        if (neighbor.abilities.includes(TEACHER_ABILITY_ID)) {
          // Teacher neighbor: pass the gain outward (never back to this unit).
          queue.push({ unitId: neighborId, sourceId: step.unitId });
        } else {
          recipients.push({ instanceId: neighborId, hpGain, atkGain });
        }
      }
    }

    return recipients;
  }

  /**
   * Teacher ability: redirect a PERMANENT stat gain away from a Teacher
   * minion onto its battlefield neighbors.
   *
   * Applies the distribution computed by
   * resolveTeacherStatRedistribution (see that method for the full rules).
   *
   * @param gainOwner The minion that (temporarily) received the gain.
   * @param hpGain The permanent HP part of the gain (may be 0).
   * @param atkGain The permanent attack part of the gain (may be 0).
   */
  public redirectTeacherStatGain(gainOwner: MinionInstance, hpGain: number, atkGain: number): void {
    const recipients = this.resolveTeacherStatRedistribution(gainOwner, hpGain, atkGain);
    if (recipients.length === 0) return; // No redirect: lacks Teacher or off-board

    // The Teacher receives nothing — its neighbors get the full amount instead.
    // A GRANDMASTER owner can never retain any of the HP gain (the caller
    // already applied it through gainHp, which clamps at 1), so only a
    // non-Grandmaster owner has the applied gain removed again.
    if (!gainOwner.hasGrandmaster()) {
      gainOwner.currentHp -= hpGain;
    }
    gainOwner.attack -= atkGain;

    for (const recipient of recipients) {
      // The "keeps it for itself" case (a Teacher with no other neighbor)
      // resolves to the gain owner — undo the removal above for it (a
      // Grandmaster owner re-applies through the 1-HP cap, so it simply
      // stays at 1).
      if (recipient.instanceId === gainOwner.instanceId) {
        gainOwner.gainHp(hpGain);
        gainOwner.attack += atkGain;
        continue;
      }
      const minion = this.minionInstances.get(recipient.instanceId);
      if (minion) {
        minion.gainHp(recipient.hpGain);
        minion.attack += recipient.atkGain;
      }
    }
  }
}

// ─── Final placement (order of death) ──────────────────────────────────────

/**
 * Anything carrying an elimination key — both the authoritative PlayerState
 * (server) and the wire PlayerData (client) match this shape, so the
 * placement order is computed from ONE shared function on both sides.
 */
export interface EliminationKeySource {
  isAlive: boolean;
  eliminationRound?: number | null;
  eliminatedDuringPreparation?: boolean;
  /** The slot the assignment writes into (PlayerState.finalPlacement). */
  finalPlacement?: number | null;
}

/**
 * Order key for the FINAL PLACEMENT: the player who survived longer is
 * always placed higher than the one who died earlier.
 *  - living player → +Infinity (beats every death);
 *  - eliminated in round N's COMBAT (loss/timeout) → N*2+1;
 *  - eliminated during the PREPARATION for round N (resignation) → N*2
 *    (below that round's combat deaths, above the previous round's);
 *  - eliminated without a recorded round (legacy state) → key 1, the
 *    earliest combat death — it ties with other unrecorded deaths and sits
 *    below every recorded one (for such states this reproduces the old
 *    "living players + 1" placement exactly).
 * Equal keys TIE (same placement number — standard competition ranking;
 * two deaths in the same round/phase are indistinguishable).
 */
export function eliminationOrderKey(p: EliminationKeySource): number {
  if (p.isAlive) return Number.POSITIVE_INFINITY;
  const round = p.eliminationRound ?? 0;
  return round * 2 + (p.eliminatedDuringPreparation ? 0 : 1);
}

/**
 * Assigns the FINAL PLACEMENT to every player (task 199 — the position
 * shown when the match ends: resign / loss / win). The order is PURELY
 * survival-based: players are sorted by the eliminationOrderKey
 * DESCENDING (the player who survived longest — the last survivor — is
 * 1st; a player eliminated while K others were still alive lands just
 * below those K, the "how many other players were left alive" rule),
 * and the TIES — players eliminated in the SAME turn (equal keys, e.g.
 * two combat losses in the same round) — are broken by a RANDOM
 * Fisher–Yates shuffle (the placement among same-turn deaths is random,
 * exactly as the spec requires). The result is a UNIQUE 1..N placement
 * per player (no shared ranks) written into `finalPlacement`.
 *
 * @param players The match's players (PlayerState on the server — the
 *   shared shape makes the function unit-testable with plain objects).
 * @param random01 A [0,1) random source (the match RNG — seeded in tests
 *   for the deterministic tie order).
 */
export function assignFinalPlacements(
  players: EliminationKeySource[],
  random01: () => number,
): void {
  // Descending by key (the survivor first). Explicit comparison — the
  // key can be Infinity (a living player), where arithmetic is NaN.
  const sorted = [...players].sort((a, b) => {
    const ka = eliminationOrderKey(a);
    const kb = eliminationOrderKey(b);
    if (ka === kb) return 0;
    return ka > kb ? -1 : 1;
  });
  // Walk the sorted list group by group (equal keys = same turn) and
  // shuffle each group before assigning the consecutive placements.
  let placement = 1;
  let i = 0;
  while (i < sorted.length) {
    let j = i + 1;
    while (j < sorted.length && eliminationOrderKey(sorted[j]) === eliminationOrderKey(sorted[i])) j++;
    const group = sorted.slice(i, j);
    // Fisher–Yates (in place on the group — sorted is a copy).
    for (let k = group.length - 1; k > 0; k--) {
      const m = Math.floor(random01() * (k + 1));
      [group[k], group[m]] = [group[m], group[k]];
    }
    for (const p of group) {
      p.finalPlacement = placement;
      placement++;
    }
    i = j;
  }
}
